import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import urlParser from 'js-video-url-parser';

import { Icon } from '@components';
import VideoOverlay from '@common/components/Paragraph/components/Video/Overlay';
import styles from './Button.module.scss';

export const Button = props => {
  const {
    href,
    type,
    children,
    theme,
    className,
    icon,
    externalLink,
    onClick,
    inheritColor,
  } = props;

  const clName = cn(styles.btn, className, {
    [styles.icon]: icon,
    [styles.primary]: type === 'primary',
    [styles.dark]: theme === 'dark',
    [styles.white]: theme === 'white',
    [styles.outline]: theme === 'outline',
    [styles.link]: type === 'link',
    [styles.cta]: type === 'cta',
    [styles.underlineLink]: type === 'underline-link',
    [styles.inheritColor]: inheritColor,
  });

  const content = icon ? <Icon type={icon} /> : children;
  const handleClick = e => onClick && onClick(e);

  const [modalVisible, setModalVisible] = useState(false);

  function toggleVideoOverlay(status) {
    return () => void setModalVisible(status);
  }

  if (href) {
    const embedUrl = urlParser.create({
      videoInfo: urlParser.parse(href),
      format: 'embed',
      params: { autoplay: 1 },
    });

    if (embedUrl) {
      return (
        <React.Fragment>
          <VideoOverlay
            onClose={toggleVideoOverlay(false)}
            visible={modalVisible}
            url={embedUrl}
          />
          <button
            className={clName}
            type="button"
            onClick={toggleVideoOverlay(true)}
          >
            {content}
          </button>
        </React.Fragment>
      );
    }

    const isAbsolute = /http:|https:|^\/\//.test(href);
    if (externalLink || isAbsolute) {
      return (
        <a
          href={href}
          className={clName}
          target={isAbsolute ? '_blank' : '_self'}
          rel={isAbsolute ? 'noopener noreferrer' : ''}
          onClick={handleClick}
        >
          {content}
        </a>
      );
    }

    return (
      <Link to={href} className={clName} onClick={handleClick}>
        {content}
      </Link>
    );
  }

  return (
    <button className={clName} onClick={handleClick}>
      {content}
    </button>
  );
};

Button.propTypes = {
  children: PropTypes.node,
  inheritColor: PropTypes.bool,
  href: PropTypes.string,
  type: PropTypes.oneOf(['link', 'underline-link', 'primary', 'cta']),
  theme: PropTypes.oneOf(['dark', 'white', 'outline']),
  className: PropTypes.string,
  icon: PropTypes.string,
  externalLink: PropTypes.bool,
  onClick: PropTypes.func,
};

Button.defaultProps = {
  href: null,
  type: null,
  theme: 'dark',
  className: null,
  icon: null,
  externalLink: false,
  onClick: null,
};
